<template>
  <section class="prices-carousel" ref="root">
    <div class="prices-carousel__inner">
      <h2 class="prices-carousel__title">Аксессуары и детали</h2>
      <div v-if="loading" class="prices-carousel__loading">
        <Loader inverse size="lg" />
      </div>
      <template v-else-if="items.length">
        <div class="prices-carousel__viewport" ref="viewport" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd">
          <div
            class="prices-carousel__track"
            :style="trackStyle"
          >
            <div
              v-for="(item, i) in items"
              :key="item.src"
              class="prices-carousel__slide"
              :style="slideStyle"
            >
              <img :src="item.src" :alt="`Фото ${i + 1}`" class="prices-carousel__img" loading="lazy" />
            </div>
          </div>
        </div>
        <div class="prices-carousel__nav">
          <button type="button" class="prices-carousel__arrow prices-carousel__arrow_prev" aria-label="Назад" @click="prev" :disabled="items.length <= visible">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <span class="prices-carousel__counter">{{ currentIndex + 1 }} — {{ Math.min(currentIndex + visible, items.length) }} / {{ items.length }}</span>
          <button type="button" class="prices-carousel__arrow prices-carousel__arrow_next" aria-label="Вперёд" @click="next" :disabled="items.length <= visible">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>
        <div v-if="items.length > visible" class="prices-carousel__dots">
          <button
            v-for="(_, i) in items"
            :key="i"
            type="button"
            class="prices-carousel__dot"
            :class="{ 'prices-carousel__dot_active': i === currentIndex }"
            :aria-label="`Слайд ${i + 1}`"
            @click="goTo(i)"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { getApiUrl } from '@/config/api'
import Loader from '@/components/Loader.vue'

export default {
  name: 'PricesCarousel',
  components: { Loader },
  setup() {
    const root = ref(null)
    const viewport = ref(null)
    const items = ref([])
    const loading = ref(true)
    const currentIndex = ref(0)
    const touchStartX = ref(0)
    const touchDeltaX = ref(0)

    const count = computed(() => items.value.length || 1)
    const visible = ref(3)
    const maxIndex = computed(() => Math.max(0, count.value - visible.value))

    // Трек (count/visible)*100% viewport; слайд (100/count)% трека = 100/visible % viewport; сдвиг 100/count % трека за шаг
    const trackStyle = computed(() => {
      const c = count.value
      const v = visible.value
      if (!c || !v) return { width: '100%', transform: 'translateX(0)' }
      const trackWidthPct = (c / v) * 100
      const stepPct = 100 / c
      return {
        width: `${trackWidthPct}%`,
        transform: `translateX(${-currentIndex.value * stepPct}%)`
      }
    })

    const slideStyle = computed(() => {
      const c = count.value
      if (!c) return {}
      const pct = 100 / c
      return { width: `${pct}%`, flexBasis: `${pct}%`, minWidth: `${pct}%` }
    })

    function setVisible() {
      const w = window.innerWidth
      visible.value = w <= 480 ? 1 : w <= 768 ? 2 : 3
    }

    function prev() {
      if (count.value <= visible.value) return
      currentIndex.value = Math.max(0, currentIndex.value - 1)
    }

    function next() {
      if (count.value <= visible.value) return
      currentIndex.value = Math.min(maxIndex.value, currentIndex.value + 1)
    }

    function goTo(i) {
      currentIndex.value = Math.min(maxIndex.value, Math.max(0, i))
    }

    function onTouchStart(e) {
      touchStartX.value = e.touches[0].clientX
      touchDeltaX.value = 0
    }

    function onTouchMove(e) {
      touchDeltaX.value = e.touches[0].clientX - touchStartX.value
    }

    function onTouchEnd() {
      const threshold = 50
      if (touchDeltaX.value > threshold) prev()
      else if (touchDeltaX.value < -threshold) next()
    }

    onMounted(async () => {
      setVisible()
      window.addEventListener('resize', setVisible)
      try {
        const res = await fetch(getApiUrl('/api/carousel'))
        const data = await res.json()
        items.value = data.items || []
      } catch (e) {
        console.error('Carousel load error', e)
      } finally {
        loading.value = false
      }
    })
    onUnmounted(() => window.removeEventListener('resize', setVisible))

    return {
      root,
      viewport,
      items,
      loading,
      currentIndex,
      visible,
      trackStyle,
      slideStyle,
      prev,
      next,
      goTo,
      onTouchStart,
      onTouchMove,
      onTouchEnd
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/_variables.scss";

$ease: cubic-bezier(0.22, 1, 0.36, 1);

.prices-carousel {
  width: 100%;
  padding: 56px 0 64px;
  background: $color-bg-alt;
  color: $color-primary-inverse;

  @media (max-width: $breakpoint-mobile) {
    padding: 40px 0 48px;
  }
}

.prices-carousel__inner {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 $gap;
}

.prices-carousel__title {
  font-size: clamp(17px, 2vw, 22px);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-align: center;
  margin: 0 0 28px;
  padding: 0;
}

.prices-carousel__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.prices-carousel__viewport {
  width: 100%;
  overflow: hidden;
  touch-action: pan-y pinch-zoom;

  @media (max-width: $breakpoint-mobile) {
    width: 100vw;
    margin-left: calc(50% - 50vw - 24px);
  }
}

.prices-carousel__track {
  display: flex;
  transition: transform 0.5s $ease;
  will-change: transform;
}

.prices-carousel__slide {
  flex: 0 0 auto;
  box-sizing: border-box;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: $breakpoint-mobile) {
    padding: 0 4px;
  }
}

.prices-carousel__img {
  width: 100%;
  height: auto;
  max-height: 380px;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
}

.prices-carousel__nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 24px;
  padding: 0;
}

.prices-carousel__arrow {
  width: 48px;
  height: 48px;
  padding: 0;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  background: transparent;
  color: $color-primary-inverse;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    border-color: $color-accent-teal;
    background: rgba(97, 130, 111, 0.3);
    color: $color-accent-teal;
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
  svg {
    width: 22px;
    height: 22px;
  }
}

.prices-carousel__counter {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.8);
  min-width: 56px;
  text-align: center;
}

.prices-carousel__dots {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  padding: 0;
}

.prices-carousel__dot {
  width: 9px;
  height: 9px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition: background 0.25s $ease, transform 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.6);
  }
  &_active {
    background: $color-accent-teal;
    transform: scale(1.2);
  }
}
</style>
